import * as XLSX from "xlsx";

export const CHART_COLORS = ["#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

export const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const avg = (list) => (list.length ? Math.round((list.reduce((a, b) => a + b, 0) / list.length) * 100) / 100 : 0);
const max = (list) => (list.length ? Math.max(...list) : 0);
const min = (list) => (list.length ? Math.min(...list) : 0);

export const isAttended = (row) => row.attended === true || row.attended === 1 || row.attended === "1";

// The API carries no explicit pass/fail field — `estatus` is used when it already
// spells out Pass/Fail, otherwise a standard accuracy threshold is applied. This is a
// documented, swappable assumption rather than a guess baked silently into the numbers.
const PASS_ACCURACY_THRESHOLD = 35;

export const resolvePassFail = (row) => {
    if (!isAttended(row)) return null;
    const status = String(row.estatus || "").trim().toLowerCase();
    if (status.includes("pass")) return "Pass";
    if (status.includes("fail")) return "Fail";
    return num(row.accuracy) >= PASS_ACCURACY_THRESHOLD ? "Pass" : "Fail";
};

export const SEGMENT_DEFS = [
    { key: "Excellent", min: 85, color: "#16a34a" },
    { key: "Good", min: 70, color: "#2563eb" },
    { key: "Average", min: 50, color: "#d97706" },
    { key: "Below Average", min: 35, color: "#f97316" },
    { key: "Weak", min: 0, color: "#dc2626" },
];

export const resolveSegment = (accuracy) => {
    const a = num(accuracy);
    for (const seg of SEGMENT_DEFS) {
        if (a >= seg.min) return seg.key;
    }
    return "Weak";
};

// Single pass over the raw getStudentTableData rows that produces every aggregate the
// analytics dashboard needs — computed once per exam (memoized by the caller) so every
// section/chart reads from the same already-derived numbers instead of recomputing.
export const buildExamAnalytics = (students, opts = {}) => {
    const { classMap = {}, sectionMap = {}, authorizedSubjectIds = [], authorizedClassIds = [], authorizedSectionIds = [] } = opts;

    // Defensive client-side scoping — the API is asked to already restrict students to
    // this lecturer's section_id(s), but a class/section check is repeated here too
    // (same "never trust a single filter path" approach used for the exams list),
    // so nothing outside the lecturer's own access ever reaches the dashboard.
    const authorizedStudents = (students || []).filter((s) => {
        if (authorizedClassIds.length && !authorizedClassIds.includes(String(s.class_id))) return false;
        if (authorizedSectionIds.length && !authorizedSectionIds.includes(String(s.section_id))) return false;
        return true;
    });

    const rows = authorizedStudents.map((s) => {
        const attended = isAttended(s);
        return {
            ...s,
            attended,
            passFail: resolvePassFail(s),
            segment: attended ? resolveSegment(s.accuracy) : null,
            class_name: classMap[s.class_id] || s.className || s.class_id,
            section_name: sectionMap[s.section_id] || s.sectionName || s.section_id,
            marksNum: num(s.marks),
            accuracyNum: num(s.accuracy),
            examRankNum: num(s.exam_rank),
            overallRankNum: num(s.overall_rank),
        };
    });

    const attendedRows = rows.filter((r) => r.attended);
    const totalStudents = rows.length;
    const attendedCount = attendedRows.length;
    const absentCount = totalStudents - attendedCount;

    const marksList = attendedRows.map((r) => r.marksNum);
    const accuracyList = attendedRows.map((r) => r.accuracyNum);
    const passCount = attendedRows.filter((r) => r.passFail === "Pass").length;
    const failCount = attendedCount - passCount;
    const sortedByExamRank = [...attendedRows].sort((a, b) => (a.examRankNum || Infinity) - (b.examRankNum || Infinity));

    const overall = {
        totalStudents,
        attended: attendedCount,
        absent: absentCount,
        attendancePercent: totalStudents ? Math.round((attendedCount / totalStudents) * 1000) / 10 : 0,
        averageMarks: avg(marksList),
        highestMarks: max(marksList),
        lowestMarks: attendedRows.length ? min(marksList) : 0,
        averageAccuracy: avg(accuracyList),
        topRank: sortedByExamRank.length ? sortedByExamRank[0].examRankNum : null,
        lastRank: sortedByExamRank.length ? sortedByExamRank[sortedByExamRank.length - 1].examRankNum : null,
        passCount,
        failCount,
        passPercent: attendedCount ? Math.round((passCount / attendedCount) * 1000) / 10 : 0,
        failPercent: attendedCount ? Math.round((failCount / attendedCount) * 1000) / 10 : 0,
    };

    const segmentCounts = SEGMENT_DEFS.reduce((acc, s) => ({ ...acc, [s.key]: 0 }), {});
    attendedRows.forEach((r) => {
        segmentCounts[r.segment] = (segmentCounts[r.segment] || 0) + 1;
    });
    const segments = SEGMENT_DEFS.map((s) => ({ key: s.key, color: s.color, count: segmentCounts[s.key] || 0 }));

    // Subjects — authorizedSubjectIds empty means unrestricted (see authHelper's
    // "empty cookie = unrestricted" convention used across this dashboard).
    const subjectMap = {};
    attendedRows.forEach((r) => {
        (r.subjectMarks || []).forEach((sm) => {
            const sid = String(sm.subjectId);
            if (authorizedSubjectIds.length && !authorizedSubjectIds.includes(sid)) return;
            if (!subjectMap[sid]) {
                subjectMap[sid] = {
                    subjectId: sid,
                    subjectName: sm.subjectName,
                    marksList: [],
                    accuracyList: [],
                    right: 0,
                    wrong: 0,
                    totalQuestions: 0,
                    rankList: [],
                    entries: [],
                };
            }
            const bucket = subjectMap[sid];
            const marks = num(sm.subjectMarks);
            const right = num(sm.subjectRight);
            const wrong = num(sm.subjectWrong);
            const totalQ = num(sm.subjectTotalQuestions);
            const accuracy = totalQ > 0 ? Math.round((right / totalQ) * 1000) / 10 : null;
            bucket.marksList.push(marks);
            if (accuracy != null) bucket.accuracyList.push(accuracy);
            bucket.right += right;
            bucket.wrong += wrong;
            bucket.totalQuestions += totalQ;
            if (sm.subjectRank != null) bucket.rankList.push(num(sm.subjectRank));
            bucket.entries.push({ studentName: r.student_name, marks });
        });
    });

    const subjects = Object.values(subjectMap)
        .map((b) => {
            const sortedEntries = [...b.entries].sort((x, y) => y.marks - x.marks);
            return {
                id: b.subjectId,
                subjectId: b.subjectId,
                subjectName: b.subjectName,
                averageMarks: avg(b.marksList),
                highestMarks: max(b.marksList),
                lowestMarks: min(b.marksList),
                averageAccuracy: avg(b.accuracyList),
                highestAccuracy: b.accuracyList.length ? max(b.accuracyList) : 0,
                lowestAccuracy: b.accuracyList.length ? min(b.accuracyList) : 0,
                rightAnswers: b.right,
                wrongAnswers: b.wrong,
                totalQuestions: b.totalQuestions,
                averageRank: avg(b.rankList),
                topStudent: sortedEntries[0] ? sortedEntries[0].studentName : "-",
                lowestStudent: sortedEntries.length ? sortedEntries[sortedEntries.length - 1].studentName : "-",
            };
        })
        .sort((a, b) => b.averageMarks - a.averageMarks);

    const groupBy = (keyFn, nameFn) => {
        const map = {};
        rows.forEach((r) => {
            const key = String(keyFn(r));
            if (!map[key]) map[key] = { key, name: nameFn(r), rows: [] };
            map[key].rows.push(r);
        });
        return Object.values(map)
            .map((g) => {
                const attended = g.rows.filter((r) => r.attended);
                const marksList2 = attended.map((r) => r.marksNum);
                const accuracyList2 = attended.map((r) => r.accuracyNum);
                const passCount2 = attended.filter((r) => r.passFail === "Pass").length;
                return {
                    id: g.key,
                    key: g.key,
                    name: g.name,
                    students: g.rows.length,
                    attended: attended.length,
                    averageMarks: avg(marksList2),
                    averageAccuracy: avg(accuracyList2),
                    highestMarks: attended.length ? max(marksList2) : 0,
                    lowestMarks: attended.length ? min(marksList2) : 0,
                    attendancePercent: g.rows.length ? Math.round((attended.length / g.rows.length) * 1000) / 10 : 0,
                    passPercent: attended.length ? Math.round((passCount2 / attended.length) * 1000) / 10 : 0,
                };
            })
            .sort((a, b) => b.averageMarks - a.averageMarks);
    };

    const classComparison = groupBy((r) => r.class_id, (r) => r.class_name);
    const sectionComparison = groupBy((r) => r.section_id, (r) => r.section_name);

    const leaderboard = [...attendedRows].sort((a, b) => b.marksNum - a.marksNum);
    const topPerformers = leaderboard.slice(0, 10);
    const lowestPerformers = [...attendedRows]
        .sort((a, b) => a.marksNum - b.marksNum)
        .slice(0, 10)
        .map((r) => {
            // Only consider subjects this lecturer actually teaches — same scoping
            // as the Subject Analytics section, so a "weak subject" call-out never
            // names a subject outside the lecturer's own access.
            const ownSubjects = authorizedSubjectIds.length
                ? (r.subjectMarks || []).filter((sm) => authorizedSubjectIds.includes(String(sm.subjectId)))
                : r.subjectMarks || [];
            const weakest = ownSubjects.length ? [...ownSubjects].sort((x, y) => num(x.subjectMarks) - num(y.subjectMarks))[0] : null;
            return { ...r, weakSubject: weakest ? weakest.subjectName : "-" };
        });

    const marksDistribution = attendedRows.map((r) => ({ name: r.student_name, marks: r.marksNum }));
    const rankDistribution = [...attendedRows]
        .filter((r) => r.examRankNum)
        .sort((a, b) => a.examRankNum - b.examRankNum)
        .slice(0, 30)
        .map((r) => ({ name: r.student_name, examRank: r.examRankNum, overallRank: r.overallRankNum }));

    return {
        rows,
        attendedRows,
        overall,
        segments,
        subjects,
        classComparison,
        sectionComparison,
        leaderboard,
        topPerformers,
        lowestPerformers,
        marksDistribution,
        rankDistribution,
    };
};

export const filterStudentRows = (rows, filters) => {
    const { search, classId, sectionIds, subjectId, attendance, passFail, quickFilter } = filters;
    const term = (search || "").trim().toLowerCase();
    let list = rows.filter((r) => {
        if (term && !String(r.student_name || "").toLowerCase().includes(term)) return false;
        if (classId && String(r.class_id) !== String(classId)) return false;
        if (sectionIds && sectionIds.length && !sectionIds.includes(String(r.section_id))) return false;
        if (subjectId && !(r.subjectMarks || []).some((sm) => String(sm.subjectId) === String(subjectId))) return false;
        if (attendance === "present" && !r.attended) return false;
        if (attendance === "absent" && r.attended) return false;
        if (passFail === "pass" && r.passFail !== "Pass") return false;
        if (passFail === "fail" && r.passFail !== "Fail") return false;
        return true;
    });
    if (quickFilter === "top") {
        list = [...list].sort((a, b) => b.marksNum - a.marksNum).slice(0, 10);
    } else if (quickFilter === "lowest") {
        list = [...list.filter((r) => r.attended)].sort((a, b) => a.marksNum - b.marksNum).slice(0, 10);
    }
    return list;
};

export const exportRowsToFile = (rows, columns, filename, type) => {
    const headerRow = columns.map((c) => c.text);
    const dataRows = rows.map((r) => columns.map((c) => (c.value ? c.value(r) : r[c.dataField])));
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    if (type === "csv") {
        XLSX.writeFile(workbook, `${filename}.csv`, { bookType: "csv" });
    } else {
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    }
};

export const printRows = (rows, columns, title) => {
    const headerRow = columns.map((c) => `<th style="padding:6px;border:1px solid #ddd;text-align:left;">${c.text}</th>`).join("");
    const bodyRows = rows
        .map(
            (r) =>
                `<tr>${columns
                    .map((c) => `<td style="padding:6px;border:1px solid #ddd;">${c.value ? c.value(r) : r[c.dataField] ?? "-"}</td>`)
                    .join("")}</tr>`
        )
        .join("");
    const html = `<html><head><title>${title}</title></head><body><h3>${title}</h3><table style="border-collapse:collapse;width:100%;font-family:sans-serif;font-size:13px;">${headerRow}${bodyRows}</table></body></html>`;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
};
